import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";

const navItems = [
  { label: "Features", to: "/features" },
  { label: "Solutions", to: "/solutions" },
  { label: "Pricing", to: "/pricing" },
  { label: "Resources", to: "/resources" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export function SiteNav() {
  const [open, setOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (active) setIsAuthenticated(!!session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setIsAuthenticated(!!session);
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium transition-colors ${
      isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70 safe-area-top">
      <div className="container mx-auto px-4 h-14 sm:h-16 flex items-center justify-between gap-4">
        <Link to="/welcome" className="flex items-center gap-2 min-w-0 min-h-[44px]" aria-label="TransportMate home">
          <span className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
            <Truck className="h-[18px] w-[18px] text-primary-foreground" />
          </span>
          <span className="text-base sm:text-lg font-bold tracking-tight truncate">TransportMate</span>
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {isAuthenticated ? (
            <Link to="/dashboard">
              <Button size="sm" className="font-semibold">Dashboard</Button>
            </Link>
          ) : (
            <>
              <Link to="/auth" className="inline-flex min-h-[44px] items-center px-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Login
              </Link>

              <Link to="/pricing#pricing">
                <Button size="sm" className="font-semibold shadow-sm">Start Free Trial</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="flex lg:hidden items-center gap-2">
          {!isAuthenticated && (
            <Link to="/pricing#pricing">
              <Button size="sm" className="h-11 font-semibold">Free Trial</Button>
            </Link>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="h-11 w-11">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm p-0 flex flex-col overflow-y-auto overscroll-contain">
              <SheetTitle className="sr-only">TransportMate navigation</SheetTitle>
              <div className="p-5 border-b flex items-center gap-2">
                <span className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                  <Truck className="h-[18px] w-[18px] text-primary-foreground" />
                </span>
                <span className="font-bold tracking-tight">TransportMate</span>
              </div>
              <nav aria-label="Mobile" className="p-3">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-3 text-[15px] font-medium transition-colors ${
                        isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <div className="mt-auto p-4 border-t space-y-2.5 pb-[max(1rem,env(safe-area-inset-bottom))]">
                {isAuthenticated ? (
                  <Link to="/dashboard" className="block">
                    <Button className="w-full font-semibold">Go to Dashboard</Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/pricing#pricing" className="block">
                      <Button className="w-full font-semibold">Start Free Trial</Button>
                    </Link>
                    <Link to="/auth" className="block">
                      <Button variant="outline" className="w-full font-semibold">Login</Button>
                    </Link>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
